import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Users, Calendar, MapPin, TrendingUp, DollarSign, CheckCircle, BarChart3, Activity, Edit3, Save, X, Search, ArrowLeft, Upload, Trash2, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getSummitEventDetails, saveSummitEventDetails, getSummitRegistrations, getSummitStats, getSummitDashboardVisibility, setSummitDashboardVisibility } from '../services/summitService';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const SummitOverview = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('registrations');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summitStats, setSummitStats] = useState({});
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTarget, setEditingTarget] = useState(null);
  const [tempTargetValue, setTempTargetValue] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
  const [uploadingActivityImages, setUploadingActivityImages] = useState({});
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showOnDashboard, setShowOnDashboard] = useState(true);
  const [dashboardCardLoading, setDashboardCardLoading] = useState(true);
  const [savingDashboardCard, setSavingDashboardCard] = useState(false);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stats, regs, eventDetails] = await Promise.all([
          getSummitStats(),
          getSummitRegistrations(),
          getSummitEventDetails()
        ]);
        
        console.log('Loaded registrations:', regs); // Debug log
        
        setSummitStats(stats || {});
        setRegistrations(regs || [
          // Sample data if API doesn't return anything
          {
            id: '1',
            primaryRegistrant: {
              firstName: 'John',
              lastName: 'Doe',
              title: 'CEO',
              email: 'john.doe@example.com'
            },
            company: 'ABC Construction',
            registrationDate: new Date('2024-01-15'),
            guests: [
              { firstName: 'Jane', lastName: 'Doe' }
            ]
          },
          {
            id: '2',
            primaryRegistrant: {
              firstName: 'Sarah',
              lastName: 'Smith',
              title: 'Operations Manager',
              email: 'sarah.smith@example.com'
            },
            company: 'XYZ Builders',
            registrationDate: new Date('2024-01-16'),
            guests: []
          }
        ]);
        setEventDetails(eventDetails || {});
      } catch (error) {
        console.error('Error loading summit data:', error);
        // Set empty defaults if API calls fail
        setSummitStats({});
        setRegistrations([]);
        setEventDetails({});
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Load whether the BBA Summit card is currently on client dashboards
  useEffect(() => {
    const loadVisibility = async () => {
      try {
        setDashboardCardLoading(true);
        setShowOnDashboard(await getSummitDashboardVisibility());
      } catch (error) {
        console.error('Error loading summit dashboard visibility:', error);
      } finally {
        setDashboardCardLoading(false);
      }
    };
    loadVisibility();
  }, []);

  const handleToggleDashboardCard = async () => {
    const nextValue = !showOnDashboard;
    try {
      setSavingDashboardCard(true);
      await setSummitDashboardVisibility(nextValue);
      setShowOnDashboard(nextValue);
    } catch (error) {
      console.error('Error updating summit dashboard visibility:', error);
      alert('Failed to update the dashboard card. Please try again.');
    } finally {
      setSavingDashboardCard(false);
    }
  };



  // Filter registrations by search term
  const filteredRegistrations = registrations.filter(reg => {
    if (!reg) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      reg.firstName?.toLowerCase().includes(searchLower) ||
      reg.lastName?.toLowerCase().includes(searchLower) ||
      reg.title?.toLowerCase().includes(searchLower) ||
      reg.email?.toLowerCase().includes(searchLower) ||
      reg.userEmail?.toLowerCase().includes(searchLower) ||
      reg.company?.toLowerCase().includes(searchLower) ||
      // Also search in guest names
      reg.guests?.some(guest => 
        guest.firstName?.toLowerCase().includes(searchLower) ||
        guest.lastName?.toLowerCase().includes(searchLower)
      )
    );
  });

  // Event details data
  const [eventDetails, setEventDetails] = useState({
    startDate: '2024-07-15',
    endDate: '2024-07-17',
    location: 'Nashville Convention Center, Nashville, TN',
    overview: {
      whyAttend: 'Join industry leaders and experts for three days of intensive learning, networking, and strategic planning. The BBA Summit is your opportunity to gain exclusive insights, connect with peers, and accelerate your business growth.',
      knowledgeSkills: [
        'Advanced business strategy and scaling techniques',
        'Latest industry trends and market analysis',
        'Financial management and optimization strategies',
        'Team building and leadership development',
        'Technology integration and automation',
        'Customer acquisition and retention strategies'
      ],
      resourcesSupport: [
        'Exclusive access to BBA resource library',
        'One-on-one consulting sessions',
        'Networking events and peer groups',
        'Follow-up support and accountability',
        'Access to expert panel discussions',
        'Comprehensive event materials and recordings'
      ]
    },
    schedule: [
      { day: 'Day 1 - July 15', events: ['Opening Keynote (9:00 AM)', 'Strategy Sessions (10:30 AM)', 'Networking Lunch (12:00 PM)', 'Workshop Sessions (2:00 PM)', 'Evening Reception (6:00 PM)'] },
      { day: 'Day 2 - July 16', events: ['Morning Keynote (8:30 AM)', 'Breakout Sessions (10:00 AM)', 'Panel Discussions (2:00 PM)', 'Interactive Workshops (4:00 PM)', 'Awards Dinner (7:00 PM)'] },
      { day: 'Day 3 - July 17', events: ['Closing Keynote (9:00 AM)', 'Strategy Implementation (10:30 AM)', 'Q&A Sessions (2:00 PM)', 'Closing Ceremony (4:00 PM)'] }
    ],
    speakers: [
      { name: 'Dr. Sarah Johnson', title: 'Business Strategy Expert', company: 'Johnson Consulting Group', bio: 'Leading authority on business scaling and strategic planning with 20+ years of experience.', image: '' },
      { name: 'Michael Chen', title: 'Technology Integration Specialist', company: 'TechBuild Solutions', bio: 'Expert in digital transformation and automation for construction businesses.', image: '' },
      { name: 'Lisa Rodriguez', title: 'Financial Management Advisor', company: 'Rodriguez Financial Group', bio: 'Specialist in financial optimization and growth strategies for small businesses.', image: '' },
      { name: 'David Thompson', title: 'Leadership Development Coach', company: 'Thompson Leadership Institute', bio: 'Dedicated to building high-performing teams and developing leadership skills.', image: '' }
    ],
    sponsors: [
      { name: 'TechBuild Solutions', tier: 'Platinum', logo: 'https://example.com/techbuild-logo.png', description: 'Leading technology solutions provider for construction businesses.' },
      { name: 'Financial Partners Group', tier: 'Gold', logo: 'https://example.com/fpg-logo.png', description: 'Specialized financial services for growing businesses.' },
      { name: 'Construction Supply Co.', tier: 'Silver', logo: 'https://example.com/csc-logo.png', description: 'Premium construction materials and equipment supplier.' }
    ],
    learningTracks: [
      {
        name: 'Business Strategy & Scaling',
        sessions: [
          { title: 'Strategic Planning Fundamentals', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Scaling Operations', duration: '90 min', speaker: 'Michael Chen' },
          { title: 'Market Expansion Strategies', duration: '90 min', speaker: 'Lisa Rodriguez' }
        ]
      },
      {
        name: 'Financial Management',
        sessions: [
          { title: 'Financial Planning & Forecasting', duration: '90 min', speaker: 'Lisa Rodriguez' },
          { title: 'Cash Flow Optimization', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Investment Strategies', duration: '90 min', speaker: 'David Thompson' }
        ]
      },
      {
        name: 'Technology & Automation',
        sessions: [
          { title: 'Digital Transformation', duration: '90 min', speaker: 'Michael Chen' },
          { title: 'Automation Implementation', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Technology ROI', duration: '90 min', speaker: 'Lisa Rodriguez' }
        ]
      }
    ],
    pricingPlans: [
      {
        name: 'Early Bird',
        price: 1295,
        availableUntil: '2024-05-31',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support']
      },
      {
        name: 'Regular',
        price: 1495,
        availableUntil: '2024-07-14',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support']
      },
      {
        name: 'Group Rate',
        price: 1195,
        availableUntil: '2024-07-14',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support', 'Group discounts']
      }
    ],
    includedActivities: [
      {
        title: 'Networking Reception',
        description: 'Join fellow attendees for an evening of networking, refreshments, and building valuable business connections.',
        image: ''
      },
      {
        title: 'Interactive Workshops',
        description: 'Participate in hands-on workshops designed to help you implement strategies learned during the conference.',
        image: ''
      },
      {
        title: 'Expert Q&A Sessions',
        description: 'Get your specific questions answered by industry experts in intimate, focused discussion groups.',
        image: ''
      }
    ]
  });

  const calculateProgress = (current, target) => {
    return target > 0 ? (current / target) * 100 : 0;
  };

  const handleSaveEventDetails = async () => {
    try {
      await saveSummitEventDetails(eventDetails);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving event details:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleTargetEdit = (targetType) => {
    setEditingTarget(targetType);
    if (targetType === 'registrations') {
      setTempTargetValue(summitStats.targetRegistrations?.toString() || '');
    } else if (targetType === 'revenue') {
      setTempTargetValue(summitStats.targetRevenue?.toString() || '');
    }
  };

  const handleTargetSave = async () => {
    try {
      const updatedStats = { ...summitStats };
      if (editingTarget === 'registrations') {
        updatedStats.targetRegistrations = parseInt(tempTargetValue) || 0;
      } else if (editingTarget === 'revenue') {
        updatedStats.targetRevenue = parseInt(tempTargetValue) || 0;
      }
      
      // Here you would typically save to your backend
      // await saveSummitStats(updatedStats);
      setSummitStats(updatedStats);
      setEditingTarget(null);
      setTempTargetValue('');
    } catch (error) {
      console.error('Error saving target:', error);
    }
  };

  const handleTargetCancel = () => {
    setEditingTarget(null);
    setTempTargetValue('');
  };

  const handleSpeakerImageUpload = async (speakerIndex, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, [speakerIndex]: true }));
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-speaker-${speakerIndex}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/speakers/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the speaker's image URL
      const newSpeakers = [...eventDetails.speakers];
      newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], image: downloadURL };
      setEventDetails({ ...eventDetails, speakers: newSpeakers });
      
    } catch (error) {
      console.error('Error uploading speaker image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImages(prev => ({ ...prev, [speakerIndex]: false }));
    }
  };

  const handleRemoveSpeakerImage = (speakerIndex) => {
    const newSpeakers = [...eventDetails.speakers];
    newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], image: '' };
    setEventDetails({ ...eventDetails, speakers: newSpeakers });
  };

  const handleHeaderImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 10MB for header images)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB.');
      return;
    }

    try {
      setUploadingHeaderImage(true);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-header-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/header/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the header image URL
      setEventDetails({ ...eventDetails, headerImage: downloadURL });
      
    } catch (error) {
      console.error('Error uploading header image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingHeaderImage(false);
    }
  };

  const handleActivityImageUpload = async (activityIndex, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 5MB for activity images)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingActivityImages(prev => ({ ...prev, [activityIndex]: true }));
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-activity-${activityIndex}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/activities/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the activity's image URL
      const newActivities = [...eventDetails.includedActivities];
      newActivities[activityIndex] = { ...newActivities[activityIndex], image: downloadURL };
      setEventDetails({ ...eventDetails, includedActivities: newActivities });
      
    } catch (error) {
      console.error('Error uploading activity image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingActivityImages(prev => ({ ...prev, [activityIndex]: false }));
    }
  };

  const handleRemoveHeaderImage = () => {
    setEventDetails({ ...eventDetails, headerImage: '' });
  };

  const handleRemoveActivityImage = (activityIndex) => {
    const newActivities = [...eventDetails.includedActivities];
    newActivities[activityIndex] = { ...newActivities[activityIndex], image: '' };
    setEventDetails({ ...eventDetails, includedActivities: newActivities });
  };

  const handleRegistrationClick = (registration) => {
    setSelectedRegistration(registration);
    setShowRegistrationModal(true);
  };

  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    setSelectedRegistration(null);
  };

  const handleDeleteRegistration = async () => {
    if (!selectedRegistration) return;
    
    if (window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      try {
        // Here you would typically call an API to delete the registration
        // await deleteSummitRegistration(selectedRegistration.id);
        
        // Remove from local state
        const updatedRegistrations = registrations.filter(reg => reg.id !== selectedRegistration.id);
        setRegistrations(updatedRegistrations);
        
        // Close modal
        closeRegistrationModal();
        
        // You might want to show a success message here
        console.log('Registration deleted successfully');
      } catch (error) {
        console.error('Error deleting registration:', error);
        alert('Failed to delete registration. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen relative text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center relative">
            <button
              onClick={() => navigate('/crm/reports')}
              className={`absolute left-0 p-2 rounded-lg ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight">Summit Overview</h1>
            </div>
            <button
              onClick={handleToggleDashboardCard}
              disabled={dashboardCardLoading || savingDashboardCard}
              title={showOnDashboard
                ? 'The BBA Summit card is showing on every client dashboard — click to hide it'
                : 'The BBA Summit card is hidden from client dashboards — click to show it'}
              className={`absolute right-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                showOnDashboard
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : darkMode
                    ? 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
              }`}
            >
              {showOnDashboard ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span>
                {dashboardCardLoading
                  ? 'Loading…'
                  : savingDashboardCard
                    ? 'Saving…'
                    : showOnDashboard
                      ? 'On Client Dashboards'
                      : 'Off Client Dashboards'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex justify-center space-x-8 overflow-x-auto">
            {['registrations', 'event details'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <BBALoader size={48} />
          </div>
        ) : (
          <>


        {/* Registrations Tab */}
        {activeTab === 'registrations' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center">All Registrations</h2>
              
              {/* Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Total Attendees Card */}
                <div className={`p-6 rounded-lg shadow-sm border ${
                  darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${
                      darkMode ? 'bg-blue-800' : 'bg-blue-100'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        darkMode ? 'text-blue-200' : 'text-blue-600'
                      }`} />
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${
                        darkMode ? 'text-blue-200' : 'text-blue-600'
                      }`}>
                        Total Attendees
                      </p>
                      <p className={`text-2xl font-bold ${
                        darkMode ? 'text-white' : 'text-blue-900'
                      }`}>
                        {registrations.reduce((total, reg) => {
                          const guestCount = reg.guests ? reg.guests.length : 0;
                          return total + 1 + guestCount;
                        }, 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Primary Registrants Card */}
                <div className={`p-6 rounded-lg shadow-sm border ${
                  darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${
                      darkMode ? 'bg-green-800' : 'bg-green-100'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        darkMode ? 'text-green-200' : 'text-green-600'
                      }`} />
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${
                        darkMode ? 'text-green-200' : 'text-green-600'
                      }`}>
                        Primary Registrants
                      </p>
                      <p className={`text-2xl font-bold ${
                        darkMode ? 'text-white' : 'text-green-900'
                      }`}>
                        {registrations.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Guests Card */}
                <div className={`p-6 rounded-lg shadow-sm border ${
                  darkMode ? 'bg-purple-900 border-purple-700' : 'bg-purple-50 border-purple-200'
                }`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${
                      darkMode ? 'bg-purple-800' : 'bg-purple-100'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        darkMode ? 'text-purple-200' : 'text-purple-600'
                      }`} />
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${
                        darkMode ? 'text-purple-200' : 'text-purple-600'
                      }`}>
                        Total Guests
                      </p>
                      <p className={`text-2xl font-bold ${
                        darkMode ? 'text-white' : 'text-purple-900'
                      }`}>
                        {registrations.reduce((total, reg) => {
                          return total + (reg.guests ? reg.guests.length : 0);
                        }, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search registrants"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>

            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
              {filteredRegistrations.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    {registrations.length === 0 ? 'No registrations found.' : 'No registrations match your search.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredRegistrations.map((registration, index) => (
                        <tr 
                          key={registration.id || index} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                          onClick={() => handleRegistrationClick(registration)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {registration.firstName} {registration.lastName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {registration.title || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {registration.email || registration.userEmail}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {registration.registrationDate instanceof Date ? registration.registrationDate.toLocaleDateString() : 
                             registration.registrationDate ? new Date(registration.registrationDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {registration.guests ? registration.guests.length + 1 : 1}
                            </span>
                            {registration.guests && registration.guests.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                +{registration.guests.length} guest{registration.guests.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">Free</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}





        {/* Event Details Tab */}
        {activeTab === 'event details' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-center flex-1">Event Details</h2>
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <button 
                      onClick={handleSaveEventDetails}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                    <button 
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Event Details</span>
                  </button>
                )}
              </div>
            </div>

            {/* Basic Event Information */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={eventDetails.startDate}
                      onChange={(e) => setEventDetails({...eventDetails, startDate: e.target.value})}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300">{eventDetails.startDate}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={eventDetails.endDate}
                      onChange={(e) => setEventDetails({...eventDetails, endDate: e.target.value})}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300">{eventDetails.endDate}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={eventDetails.location}
                      onChange={(e) => setEventDetails({...eventDetails, location: e.target.value})}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300">{eventDetails.location}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Header Image</label>
                  {isEditing ? (
                    <div>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-32 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                            {eventDetails.headerImage ? (
                              <img 
                                src={eventDetails.headerImage} 
                                alt="Header" 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Upload className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          {uploadingHeaderImage && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex gap-2">
                            <label className="cursor-pointer bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors inline-flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              {eventDetails.headerImage ? 'Change Image' : 'Upload Image'}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleHeaderImageUpload(e.target.files[0])}
                                className="hidden"
                                disabled={uploadingHeaderImage}
                              />
                            </label>
                            {eventDetails.headerImage && (
                              <button
                                onClick={handleRemoveHeaderImage}
                                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                                disabled={uploadingHeaderImage}
                              >
                                <X className="w-4 h-4" />
                                Remove
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            JPEG, PNG, GIF, WebP up to 10MB
                          </p>
                        </div>
                      </div>
                      {eventDetails.headerImage && (
                        <div className="mt-4">
                          <img 
                            src={eventDetails.headerImage} 
                            alt="Header Preview" 
                            className="w-full h-48 object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {eventDetails.headerImage ? (
                        <img 
                          src={eventDetails.headerImage} 
                          alt="Header" 
                          className="w-full h-48 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center">
                          <div className="text-center">
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400">No header image set</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Overview Section */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Overview</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Why Attend the BBA Summit?</label>
                  {isEditing ? (
                    <textarea
                      value={eventDetails.overview.whyAttend}
                      onChange={(e) => setEventDetails({
                        ...eventDetails, 
                        overview: {...eventDetails.overview, whyAttend: e.target.value}
                      })}
                      rows={4}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300">{eventDetails.overview.whyAttend}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">What You'll Gain - Knowledge & Skills</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {eventDetails.overview.knowledgeSkills.map((skill, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={skill}
                            onChange={(e) => {
                              const newSkills = [...eventDetails.overview.knowledgeSkills];
                              newSkills[index] = e.target.value;
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                              });
                            }}
                            className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                          />
                          <button
                            onClick={() => {
                              const newSkills = eventDetails.overview.knowledgeSkills.filter((_, i) => i !== index);
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                              });
                            }}
                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newSkills = [...eventDetails.overview.knowledgeSkills, ''];
                          setEventDetails({
                            ...eventDetails,
                            overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                          });
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Add Skill
                      </button>
                    </div>
                  ) : (
                    <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                      {eventDetails.overview.knowledgeSkills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Resources & Support</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {eventDetails.overview.resourcesSupport.map((resource, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={resource}
                            onChange={(e) => {
                              const newResources = [...eventDetails.overview.resourcesSupport];
                              newResources[index] = e.target.value;
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, resourcesSupport: newResources}
                              });
                            }}
                            className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                          />
                          <button
                            onClick={() => {
                              const newResources = eventDetails.overview.resourcesSupport.filter((_, i) => i !== index);
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, resourcesSupport: newResources}
                              });
                            }}
                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newResources = [...eventDetails.overview.resourcesSupport, ''];
                          setEventDetails({
                            ...eventDetails,
                            overview: {...eventDetails.overview, resourcesSupport: newResources}
                          });
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Add Resource
                      </button>
                    </div>
                  ) : (
                    <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                      {eventDetails.overview.resourcesSupport.map((resource, index) => (
                        <li key={index}>{resource}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Conference Schedule */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Conference Schedule</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.schedule.map((day, dayIndex) => (
                    <div key={dayIndex} className="border border-gray-300 rounded-lg p-4">
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={day.day}
                          onChange={(e) => {
                            const newSchedule = [...eventDetails.schedule];
                            newSchedule[dayIndex] = {...day, day: e.target.value};
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newSchedule = eventDetails.schedule.filter((_, i) => i !== dayIndex);
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Day
                        </button>
                      </div>
                      <div className="space-y-2">
                        {day.events.map((event, eventIndex) => (
                          <div key={eventIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={event}
                              onChange={(e) => {
                                const newSchedule = [...eventDetails.schedule];
                                newSchedule[dayIndex] = {
                                  ...day,
                                  events: day.events.map((evt, i) => i === eventIndex ? e.target.value : evt)
                                };
                                setEventDetails({...eventDetails, schedule: newSchedule});
                              }}
                              className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                darkMode 
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                  : 'border-gray-300 bg-white text-gray-900'
                              }`}
                            />
                            <button
                              onClick={() => {
                                const newSchedule = [...eventDetails.schedule];
                                newSchedule[dayIndex] = {
                                  ...day,
                                  events: day.events.filter((_, i) => i !== eventIndex)
                                };
                                setEventDetails({...eventDetails, schedule: newSchedule});
                              }}
                              className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const newSchedule = [...eventDetails.schedule];
                            newSchedule[dayIndex] = {
                              ...day,
                              events: [...day.events, '']
                            };
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          Add Event
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newSchedule = [...eventDetails.schedule, { day: '', events: [''] }];
                      setEventDetails({...eventDetails, schedule: newSchedule});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Day
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {eventDetails.schedule.map((day, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <h4 className="font-semibold mb-2">{day.day}</h4>
                      <ul className="space-y-1 text-gray-600 dark:text-gray-300">
                        {day.events.map((event, eventIndex) => (
                          <li key={eventIndex} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {event}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Featured Speakers */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Featured Speakers</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.speakers.map((speaker, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Name"
                          value={speaker.name}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, name: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Title"
                          value={speaker.title}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, title: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Company"
                          value={speaker.company}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, company: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newSpeakers = eventDetails.speakers.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Speaker
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">Speaker Image</label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                              {speaker.image ? (
                                <img 
                                  src={speaker.image} 
                                  alt={speaker.name} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Upload className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                            {uploadingImages[index] && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex gap-2">
                              <label className="cursor-pointer bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors inline-flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                {speaker.image ? 'Change Image' : 'Upload Image'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleSpeakerImageUpload(index, e.target.files[0])}
                                  className="hidden"
                                  disabled={uploadingImages[index]}
                                />
                              </label>
                              {speaker.image && (
                                <button
                                  onClick={() => handleRemoveSpeakerImage(index)}
                                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                                  disabled={uploadingImages[index]}
                                >
                                  <X className="w-4 h-4" />
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              JPEG, PNG, GIF, WebP up to 5MB
                            </p>
                          </div>
                        </div>
                      </div>
                      <textarea
                        placeholder="Bio"
                        value={speaker.bio}
                        onChange={(e) => {
                          const newSpeakers = [...eventDetails.speakers];
                          newSpeakers[index] = {...speaker, bio: e.target.value};
                          setEventDetails({...eventDetails, speakers: newSpeakers});
                        }}
                        rows={3}
                        className={`w-full mt-4 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newSpeakers = [...eventDetails.speakers, { name: '', title: '', company: '', bio: '', image: '' }];
                      setEventDetails({...eventDetails, speakers: newSpeakers});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Speaker
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {eventDetails.speakers.map((speaker, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          {speaker.image ? (
                            <img 
                              src={speaker.image} 
                              alt={speaker.name} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{speaker.name}</h4>
                          <p className="text-red-600 dark:text-red-400 text-sm">{speaker.title}</p>
                          <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{speaker.company}</p>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">{speaker.bio}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sponsors */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Sponsors</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.sponsors.map((sponsor, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Sponsor Name"
                          value={sponsor.name}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, name: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <select
                          value={sponsor.tier}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, tier: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        >
                          <option value="Platinum">Platinum</option>
                          <option value="Gold">Gold</option>
                          <option value="Silver">Silver</option>
                          <option value="Bronze">Bronze</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Logo URL"
                          value={sponsor.logo}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, logo: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newSponsors = eventDetails.sponsors.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Sponsor
                        </button>
                      </div>
                      <textarea
                        placeholder="Description"
                        value={sponsor.description}
                        onChange={(e) => {
                          const newSponsors = [...eventDetails.sponsors];
                          newSponsors[index] = {...sponsor, description: e.target.value};
                          setEventDetails({...eventDetails, sponsors: newSponsors});
                        }}
                        rows={3}
                        className={`w-full mt-4 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newSponsors = [...eventDetails.sponsors, { name: '', tier: 'Bronze', logo: '', description: '' }];
                      setEventDetails({...eventDetails, sponsors: newSponsors});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Sponsor
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {eventDetails.sponsors.map((sponsor, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
                      <div className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full mb-2 ${
                        sponsor.tier === 'Platinum' ? 'bg-purple-100 text-purple-800' :
                        sponsor.tier === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
                        sponsor.tier === 'Silver' ? 'bg-gray-100 text-gray-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {sponsor.tier}
                      </div>
                      <h4 className="font-semibold mb-2">{sponsor.name}</h4>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{sponsor.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Learning Tracks */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Learning Tracks</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.learningTracks.map((track, trackIndex) => (
                    <div key={trackIndex} className="border border-gray-300 rounded-lg p-4">
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          placeholder="Track Name"
                          value={track.name}
                          onChange={(e) => {
                            const newTracks = [...eventDetails.learningTracks];
                            newTracks[trackIndex] = {...track, name: e.target.value};
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newTracks = eventDetails.learningTracks.filter((_, i) => i !== trackIndex);
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Track
                        </button>
                      </div>
                      <div className="space-y-2">
                        {track.sessions.map((session, sessionIndex) => (
                          <div key={sessionIndex} className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Session Name"
                              value={session}
                              onChange={(e) => {
                                const newTracks = [...eventDetails.learningTracks];
                                newTracks[trackIndex] = {
                                  ...track,
                                  sessions: track.sessions.map((s, i) => i === sessionIndex ? e.target.value : s)
                                };
                                setEventDetails({...eventDetails, learningTracks: newTracks});
                              }}
                              className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                darkMode 
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                  : 'border-gray-300 bg-white text-gray-900'
                              }`}
                            />
                            <button
                              onClick={() => {
                                const newTracks = [...eventDetails.learningTracks];
                                newTracks[trackIndex] = {
                                  ...track,
                                  sessions: track.sessions.filter((_, i) => i !== sessionIndex)
                                };
                                setEventDetails({...eventDetails, learningTracks: newTracks});
                              }}
                              className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const newTracks = [...eventDetails.learningTracks];
                            newTracks[trackIndex] = {
                              ...track,
                              sessions: [...track.sessions, '']
                            };
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          Add Session
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newTracks = [...eventDetails.learningTracks, { name: '', sessions: [''] }];
                      setEventDetails({...eventDetails, learningTracks: newTracks});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Track
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {eventDetails.learningTracks.map((track, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">{track.name}</h4>
                      <ul className="space-y-2">
                        {track.sessions.map((session, sessionIndex) => (
                          <li key={sessionIndex} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {session}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Registration Pricing Plans */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Registration Pricing Plans</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.pricingPlans.map((plan, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Plan Name"
                          value={plan.name}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, name: e.target.value};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <input
                          type="number"
                          placeholder="Price"
                          value={plan.price}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, price: parseInt(e.target.value) || 0};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <input
                          type="date"
                          placeholder="Available Until"
                          value={plan.availableUntil}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, availableUntil: e.target.value};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newPlans = eventDetails.pricingPlans.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Plan
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">Features</label>
                        <div className="space-y-2">
                          {plan.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Feature"
                                value={feature}
                                onChange={(e) => {
                                  const newPlans = [...eventDetails.pricingPlans];
                                  newPlans[index] = {
                                    ...plan,
                                    features: plan.features.map((f, i) => i === featureIndex ? e.target.value : f)
                                  };
                                  setEventDetails({...eventDetails, pricingPlans: newPlans});
                                }}
                                className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                  darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                    : 'border-gray-300 bg-white text-gray-900'
                                }`}
                              />
                              <button
                                onClick={() => {
                                  const newPlans = [...eventDetails.pricingPlans];
                                  newPlans[index] = {
                                    ...plan,
                                    features: plan.features.filter((_, i) => i !== featureIndex)
                                  };
                                  setEventDetails({...eventDetails, pricingPlans: newPlans});
                                }}
                                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newPlans = [...eventDetails.pricingPlans];
                              newPlans[index] = {
                                ...plan,
                                features: [...plan.features, '']
                              };
                              setEventDetails({...eventDetails, pricingPlans: newPlans});
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                          >
                            Add Feature
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newPlans = [...eventDetails.pricingPlans, { 
                        name: '', 
                        price: 0, 
                        availableUntil: '', 
                        features: [''] 
                      }];
                      setEventDetails({...eventDetails, pricingPlans: newPlans});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {eventDetails.pricingPlans.map((plan, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                      <h4 className="font-semibold text-lg mb-2">{plan.name}</h4>
                      <div className="mb-4">
                        <span className="text-3xl font-bold">{formatCurrency(plan.price)}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Available until {plan.availableUntil}</p>
                      <ul className="space-y-2 text-left">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Included Activities */}
            <div className={`p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
              <h3 className="text-lg font-semibold mb-4">Included Activities</h3>
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.includedActivities?.map((activity, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Activity Title"
                          value={activity.title}
                          onChange={(e) => {
                            const newActivities = [...eventDetails.includedActivities];
                            newActivities[index] = {...activity, title: e.target.value};
                            setEventDetails({...eventDetails, includedActivities: newActivities});
                          }}
                          className={`p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => {
                            const newActivities = eventDetails.includedActivities.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, includedActivities: newActivities});
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Remove Activity
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">Activity Image</label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-24 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                              {activity.image ? (
                                <img 
                                  src={activity.image} 
                                  alt={activity.title} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Upload className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                            {uploadingActivityImages[index] && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex gap-2">
                              <label className="cursor-pointer bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors inline-flex items-center gap-2 text-sm">
                                <Upload className="w-4 h-4" />
                                {activity.image ? 'Change' : 'Upload'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleActivityImageUpload(index, e.target.files[0])}
                                  className="hidden"
                                  disabled={uploadingActivityImages[index]}
                                />
                              </label>
                              {activity.image && (
                                <button
                                  onClick={() => handleRemoveActivityImage(index)}
                                  className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 transition-colors inline-flex items-center gap-2 text-sm"
                                  disabled={uploadingActivityImages[index]}
                                >
                                  <X className="w-4 h-4" />
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              JPEG, PNG, GIF, WebP up to 5MB
                            </p>
                          </div>
                        </div>
                        {activity.image && (
                          <div className="mt-3">
                            <img 
                              src={activity.image} 
                              alt={`${activity.title} Preview`} 
                              className="w-32 h-24 object-cover rounded-lg border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <textarea
                        placeholder="Activity Description"
                        value={activity.description}
                        onChange={(e) => {
                          const newActivities = [...eventDetails.includedActivities];
                          newActivities[index] = {...activity, description: e.target.value};
                          setEventDetails({...eventDetails, includedActivities: newActivities});
                        }}
                        rows={3}
                        className={`w-full mt-4 p-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newActivities = [...(eventDetails.includedActivities || []), { title: '', description: '', image: '' }];
                      setEventDetails({...eventDetails, includedActivities: newActivities});
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Add Activity
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {eventDetails.includedActivities?.map((activity, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        {activity.image ? (
                          <img 
                            src={activity.image} 
                            alt={activity.title} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="text-center">
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No image</p>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold mb-2">{activity.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Registration Details Modal */}
      {showRegistrationModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-center items-center mb-6 relative">
                <h3 className="text-xl font-bold">Registration Details</h3>
                <button
                  onClick={closeRegistrationModal}
                  className={`absolute right-0 p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Registration Information */}
              <div className="space-y-6">
                {/* Primary Registrant */}
                <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <h4 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Primary Registrant</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedRegistration.firstName} {selectedRegistration.lastName}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedRegistration.title || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedRegistration.email || selectedRegistration.userEmail || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Registration Date</label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedRegistration.registrationDate instanceof Date ? 
                          selectedRegistration.registrationDate.toLocaleDateString() : 
                          selectedRegistration.registrationDate ? 
                            new Date(selectedRegistration.registrationDate).toLocaleDateString() : 
                            'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Guests */}
                {selectedRegistration.guests && selectedRegistration.guests.length > 0 && (
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <h4 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400 text-center">
                      Guests ({selectedRegistration.guests.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedRegistration.guests.map((guest, index) => (
                        <div key={index} className="p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {guest.firstName} {guest.lastName}
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                                  <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {guest.email || 'N/A'}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
                                  <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {guest.title || 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Information */}
                <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                  <h4 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400 text-center">Registration Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {selectedRegistration.guests ? selectedRegistration.guests.length + 1 : 1}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Total Attendees</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">Free</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Registration Cost</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {selectedRegistration.guests ? selectedRegistration.guests.length : 0}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Guests</div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                {(selectedRegistration.phone || selectedRegistration.address) && (
                  <div className={`p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <h4 className="text-lg font-semibold mb-4 text-purple-600 dark:text-purple-400">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedRegistration.phone && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Phone</label>
                          <p className="text-gray-900 dark:text-white">{selectedRegistration.phone}</p>
                        </div>
                      )}
                      {selectedRegistration.address && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Address</label>
                          <p className="text-gray-900 dark:text-white">{selectedRegistration.address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={handleDeleteRegistration}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Registration
                </button>
                <button
                  onClick={closeRegistrationModal}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    darkMode 
                      ? 'bg-gray-600 text-white hover:bg-gray-700' 
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SummitOverview; 